import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toDateOnly, type CalendarMonth } from '@odo/shared-ui';
import {
    fetchAdminAppointment,
    fetchAdminAppointments,
    fetchAdminAppointmentsCount
} from '../services/adminAppointments';
import {
    ADMIN_APPOINTMENT_DETAIL_KEY,
    ADMIN_APPOINTMENTS_KEY,
    ADMIN_APPOINTMENTS_COUNT_KEY
} from './queryKeys';

const WINDOW_MONTHS = 6;
const ONE_MINUTE = 60 * 1000;

function addMonths(date: Date, months: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
}

interface AppointmentsWindow {
    pastFrom: string;
    pastTo: string;
    upcomingFrom: string;
    upcomingTo: string;
    minMonth: CalendarMonth;
    maxMonth: CalendarMonth;
}

// Single source for the ±6-month window so the fetch ranges, the month clamp and the
// badge count can't drift. Both bounds are inclusive server-side, so the past half ends yesterday.
function getAppointmentsWindow(): AppointmentsWindow {
    const now = new Date();
    const fromDate = addMonths(now, -WINDOW_MONTHS);
    const toDate = addMonths(now, WINDOW_MONTHS);
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    return {
        pastFrom: toDateOnly(fromDate),
        pastTo: toDateOnly(yesterday),
        upcomingFrom: toDateOnly(now),
        upcomingTo: toDateOnly(toDate),
        minMonth: { year: fromDate.getFullYear(), month: fromDate.getMonth() },
        maxMonth: { year: toDate.getFullYear(), month: toDate.getMonth() }
    };
}

// Revalidates on every modal open; cached rows render meanwhile (gcTime keeps them 4 h).
function useAppointmentsRange(from: string, to: string, enabled: boolean) {
    return useQuery({
        queryKey: ADMIN_APPOINTMENTS_KEY(from, to),
        queryFn: () => fetchAdminAppointments({ from, to }),
        enabled,
        staleTime: 0
    });
}

// Backs the calendar modal: today -> +6 months first, then the past 6 months in the background
// (at once if `includePast`); only a shown past tab surfaces the past range's loading or error.
export function useAdminAppointments(includePast: boolean) {
    const fetchWindow = useMemo(getAppointmentsWindow, []);
    const upcoming = useAppointmentsRange(fetchWindow.upcomingFrom, fetchWindow.upcomingTo, true);
    const past = useAppointmentsRange(
        fetchWindow.pastFrom,
        fetchWindow.pastTo,
        includePast || upcoming.isFetchedAfterMount
    );

    const appointments = useMemo(
        () => [...(past.data ?? []), ...(upcoming.data ?? [])],
        [past.data, upcoming.data]
    );
    const ranges = includePast ? [upcoming, past] : [upcoming];

    return {
        appointments,
        isLoading: ranges.some((range) => range.isLoading),
        // A failed revalidation keeps its cached rows, so only a range with no data is an error.
        isError: ranges.some((range) => range.isError && range.data === undefined),
        refetch: () => ranges.forEach((range) => void range.refetch()),
        minMonth: fetchWindow.minMonth,
        maxMonth: fetchWindow.maxMonth
    };
}

// The list rows carry no origin event payload; the detail pane reads it from here.
export function useAdminAppointmentDetail(appointmentId: string | null) {
    return useQuery({
        queryKey: ADMIN_APPOINTMENT_DETAIL_KEY(appointmentId ?? ''),
        queryFn: () => fetchAdminAppointment(appointmentId as string),
        enabled: !!appointmentId
    });
}

/**
 * Backs the Work Queue toolbar's entry-button badge. One cheap COUNT query,
 * always enabled - errors resolve to 0 rather than surfacing to the badge.
 */
export function useAdminAppointmentsUpcomingCount(): number {
    // Computed per render (not memoized) so a long-lived tab rolls over at midnight.
    const { upcomingFrom: from, upcomingTo: to } = getAppointmentsWindow();

    const { data, isError } = useQuery({
        queryKey: ADMIN_APPOINTMENTS_COUNT_KEY(from, to),
        queryFn: () => fetchAdminAppointmentsCount({ from, to }),
        staleTime: ONE_MINUTE
    });

    return isError ? 0 : (data ?? 0);
}
